import type { AckPayloads, KnowledgeBaseView, KnowledgeDocument, LendingRequest, ListingState } from '@ralph/contracts'
import { SUPPORTED_FORMATS_SENTENCE } from '@ralph/contracts'
import type { RemedyKey } from '@/components/knowledge/knowledge-console.ts'
import { pluralCategory } from '@/i18n/format.ts'
import type { Locale } from '@/i18n/locale.ts'

/**
 * `knowledge` — every sentence the knowledge surfaces show (PRD-26 *The interface* → Knowledge;
 * AGM-124: copy lives in catalogues, never in components). The connected-folders tab keeps its
 * own namespace (`knowledgeFolders`); everything else on `/knowledge` is here, including the
 * document drawer and the console's health sentence, which had no catalogue until PRD-27 (F-14).
 *
 * English is the source of truth AND the type (PRD-27 LOC-011): `Messages = typeof en`, so the
 * Portuguese sibling is checked against the shape of this file and a missing key is a compile
 * error rather than a runtime fallback.
 *
 * The vocabulary is D-37's and it is load-bearing. "Knowledge base", never "source"; "group",
 * never "team"; "Who can use it"; "everyone who signs in"; "Send for review", "Waiting for
 * review", "sharing request"; "Agents that use it" and "Let it answer for everyone it reaches" —
 * never "lend", never "maintainer". A person who reads the builder, the Groups page and this page
 * must meet one set of words for one set of ideas. Portuguese has its own ratified list under the
 * same discipline (ADR-0018): *base de conhecimento* (f.), *grupo* (m.), *documento* (m.),
 * *trecho* (m.), *agente* (m.), *pessoa* (f.).
 *
 * Anything that interpolates is a FUNCTION returning a whole sentence, never a template assembled
 * at the call site (LOC-013) — a translator handed 'Good ' and ', ' cannot produce a language
 * whose greeting puts the name first.
 */

/**
 * What a sentence needs from the reader's locale: the grouped count, which plural form the count
 * takes, and the locale's own way of joining a list (PRD-27 LOC-016, LOC-020).
 *
 * Declared structurally from `Locale` rather than as `Pick<I18n, …>`, because `I18n` carries `t`
 * and `t` is `typeof en` — which includes this file. The narrow shape keeps the catalogue out of
 * its own type, and `useI18n()` satisfies it whole, so a component passes the hook's value and
 * nothing threads a locale by hand.
 */
export type KnowledgeFmt = {
  locale: Locale
  formatCount: (value: number) => string
  formatList: (items: readonly string[]) => string
}

/**
 * Each noun owns both its forms and asks CLDR which one this count takes.
 *
 * Never `count === 1`: `Intl.PluralRules('pt-BR').select(0)` answers `one`, so Portuguese takes
 * the SINGULAR at zero ("0 documento") while English takes the plural ("0 documents"). A
 * two-branch comparison is therefore already wrong in one of the two shipped languages, on
 * every empty state in the product, and nothing fails when it is.
 */
const isOne = (fmt: KnowledgeFmt, count: number): boolean => pluralCategory(fmt.locale, count) === 'one'

const people = (fmt: KnowledgeFmt, count: number): string =>
  `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'person' : 'people'}`
const documents = (fmt: KnowledgeFmt, count: number): string =>
  `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'document' : 'documents'}`
const bases = (fmt: KnowledgeFmt, count: number): string =>
  `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'knowledge base' : 'knowledge bases'}`
const passages = (fmt: KnowledgeFmt, count: number): string =>
  `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'passage' : 'passages'}`

/**
 * The formats a person may add, as one phrase.
 *
 * `SUPPORTED_FORMATS_SENTENCE` composes the list in `@ralph/contracts` with a hardcoded " and "
 * and two translatable common nouns inside it — "images" and "plain text" (PRD-27 F-16). Which
 * formats the reader accepts is the server's to decide and is read from the contract here; the
 * WORDS are the catalogue's, so the Portuguese sibling writes its own phrase rather than showing
 * an English list inside a Portuguese sentence. When F-16 lands in contracts this becomes a
 * function of the label list; the two call sites below do not move.
 */
const FORMATS = SUPPORTED_FORMATS_SENTENCE

/**
 * An agent's lifecycle state, in the words the builder uses for it — the same list as
 * `marketplace.maker`, which this surface reads its badge from.
 *
 * `published` is deliberately empty: a live agent wears no badge. `satisfies Record<ListingState,
 * string>` so a new state is a compile error rather than a raw `under_review` on a badge
 * (PRD-27 D-9, F-8). The lookup itself — the wire carries `z.string()`, not the enum — lives in
 * `knowledge-base.ts` with the rest of the logic (D-25).
 */
const AGENT_STATE = {
  draft: 'Draft',
  published: '',
  archived: 'Retired',
  suspended: 'Paused by an administrator'
} satisfies Record<ListingState, string>

/**
 * Letting an agent answer from a base, said five ways (PRD-26 D-16) — and the record of it
 * (PRD-27 D-7).
 *
 * Module-local, because `lend.disclosure` composes the same five sentences the four entries
 * beside it expose one at a time, and an object that reads itself inside its own initializer
 * costs this file the inferred type `Messages = typeof en` is built from. The dialog and the
 * audit replay therefore run one implementation, which is the only way the record and the
 * evidence can be guaranteed to match.
 */
type LendingAck = AckPayloads['ack.lending']

const lendMaintainedBy = (agent: string, owner: { kind: 'user' | 'group'; name: string }): string =>
  owner.kind === 'group'
    ? `${agent} is maintained by ${owner.name}’s managers.`
    : `${agent} is maintained by ${owner.name}.`

const LEND_ACTS = 'It can act as well as answer — send, change or create things where it is connected.'
const LEND_DELEGATES = 'It can hand questions on to other agents.'
const LEND_ACTS_AND_DELEGATES =
  'It can act as well as answer — send, change or create things where it is connected — and hand questions on to other agents.'
const LEND_ANSWERS_ONLY = 'It only answers; it cannot act.'

const lendCapability = (acts: boolean, delegates: boolean): string =>
  acts && delegates ? LEND_ACTS_AND_DELEGATES : acts ? LEND_ACTS : delegates ? LEND_DELEGATES : LEND_ANSWERS_ONLY

const lendReachNow = (fmt: KnowledgeFmt, reach: number, beyond: number, base: string): string =>
  reach === 0
    ? 'It is not shared with anyone yet, so today it reaches nobody.'
    : beyond === 0
      ? `It reaches ${people(fmt, reach)} now, and all of them can already use ${base}.`
      : `It reaches ${people(fmt, reach)} now; ${fmt.formatCount(beyond)} of them cannot use ${base} themselves.`

const lendReachNotLive = (base: string): string =>
  `It is not in use now, so today it reaches nobody. When it is, everyone it reaches gets answers from ${base} through it.`

const lendGrows = (base: string): string =>
  `Its reach can grow without asking you: when its maintainers share it with more people, they get answers from ${base} too.`

const LEND_RECALL = 'You can stop this at any time. Stopping does not recall answers it has already given.'

export const knowledge = {
  // ---------------------------------------------------------------------------
  // The page and its list
  // ---------------------------------------------------------------------------
  page: {
    title: 'Knowledge',
    lead: 'The knowledge bases you maintain and the ones shared with you. An agent can only cite what is in them.',
    newBase: 'New knowledge base',
    local: 'read here · nothing leaves',
    localHint: 'Documents are read and indexed inside this deployment; nothing in them is sent anywhere else.',
    searchLabel: 'Search knowledge bases',
    searchPlaceholder: 'Search by name or description',
    clearSearch: 'Clear the search',
    viewsLabel: 'Which knowledge bases',
    views: { maintain: 'You maintain', use: 'You can use', all: 'All' } satisfies Record<KnowledgeBaseView, string>,
    /** Said after a view's name to a screen reader: "You maintain, 3 knowledge bases", not "You maintain3". */
    viewCount: (fmt: KnowledgeFmt, count: number): string => bases(fmt, count),
    loading: 'Loading knowledge bases…',
    failed: 'Knowledge bases could not be loaded.',
    failedBody: 'This is a problem reading the list, not a list with nothing in it. Try again in a moment.',
    empty: {
      maintain: 'You do not maintain a knowledge base yet.',
      use: 'Nothing is shared with you yet.',
      all: 'There are no knowledge bases yet.'
    } satisfies Record<KnowledgeBaseView, string>,
    emptyBody: {
      maintain:
        'Make one for the documents your agents should cite. At first only you can use it; you decide who else can.',
      use: 'When someone shares a knowledge base with a group you are in, or with everyone, it appears here.',
      all: 'When anyone makes a knowledge base, it appears here.'
    } satisfies Record<KnowledgeBaseView, string>,
    noMatch: (q: string): string => `No knowledge base here matches “${q}”.`,
    noMatchBody: 'Check the spelling, or look in the other list.',
    pickOne: 'Choose a knowledge base',
    pickOneBody: 'See what it holds, search it the way an agent does, and — where you maintain it — who can use it.',
    back: 'All knowledge bases',
    deleted: (name: string): string => `${name} was deleted. The agents that used it keep working without it.`,
    dismiss: 'Dismiss',
    signInFailed: 'That sign-in did not finish.',
    signInFailedBody:
      'Nothing was connected and nothing here changed. Open the knowledge base you were connecting a folder to and try again.'
  },

  /** The standing mark's words: never colour alone (AGM-124). */
  standing: {
    empty: 'Empty',
    reading: 'Being read',
    silent: 'Nothing answering',
    attention: 'Needs attention',
    stale: 'Out of date',
    ready: 'Answering'
  },

  row: {
    noDocuments: 'No documents yet',
    loadingFromFolder: 'Loading from a connected folder',
    folderNeedsPerson: (fmt: KnowledgeFmt, count: number): string =>
      isOne(fmt, count)
        ? 'A connected folder needs attention'
        : `${fmt.formatCount(count)} connected folders need attention`,
    allAnswering: (fmt: KnowledgeFmt, count: number): string =>
      isOne(fmt, count) ? `${fmt.formatCount(count)} document, answering` : `${documents(fmt, count)}, all answering`,
    someAnswering: (fmt: KnowledgeFmt, answering: number, total: number): string =>
      `${fmt.formatCount(answering)} of ${documents(fmt, total)} answering`,
    readingRest: (fmt: KnowledgeFmt, answering: number, total: number): string =>
      answering === 0
        ? `Reading ${documents(fmt, total)}…`
        : `${fmt.formatCount(answering)} of ${fmt.formatCount(total)} answering · reading the rest`,
    nothingAnswering: (fmt: KnowledgeFmt, total: number): string => `${documents(fmt, total)}, none answering`,
    notUpdated: (when: string): string => `last added ${when}`,
    folders: (fmt: KnowledgeFmt, count: number): string =>
      isOne(fmt, count) ? `${fmt.formatCount(count)} connected folder` : `${fmt.formatCount(count)} connected folders`,
    waitingForReview: 'waiting for review',
    requestsToDecide: (fmt: KnowledgeFmt, count: number): string =>
      isOne(fmt, count) ? `${fmt.formatCount(count)} request to decide` : `${fmt.formatCount(count)} requests to decide`
  },

  /** Why the reader can use a base, and who else can (GRP-102: a group is always named, never an id). */
  why: {
    onlyYou: 'Only you',
    yours: 'Yours',
    ownedBy: (owner: string): string => `Owned by ${owner}`,
    onlyItsManagers: 'only its managers',
    onlyItsOwner: 'only its owner',
    // `Intl.ListFormat`, which replaced the three `joinNames` copies (LOC-016). English gains
    // its serial comma here — "Legal, Sales, and 1 more" — and Portuguese, which has none, gets
    // "Legal, Sales e mais 1" rather than an English "and" inside a Portuguese sentence.
    sharedWith: (fmt: KnowledgeFmt, names: string[], more: number): string =>
      `shared with ${fmt.formatList(more > 0 ? [...names, `${fmt.formatCount(more)} more`] : names)}`,
    sharedWithEveryone: 'shared with everyone',
    sharedWithYou: (fmt: KnowledgeFmt, names: string[], more: number): string =>
      `Shared with ${fmt.formatList(more > 0 ? [...names, `${fmt.formatCount(more)} more`] : names)}`,
    // Three branches rather than two: "both" is a word English has for exactly two and
    // Portuguese does not, so the count decides between three whole sentences, never a suffix.
    includesYou: (fmt: KnowledgeFmt, count: number): string =>
      isOne(fmt, count) ? 'includes you' : count === 2 ? 'you are in both' : `you are in all ${fmt.formatCount(count)}`,
    everyone: 'Shared with everyone',
    administrator: 'you can open it as an administrator',
    notForYou: 'not shared with you',
    /** "Only you can use it", "Only Finance EMEA’s managers can use it". */
    onlyCanUse: (who: string): string => `Only ${who} can use it`
  },

  // ---------------------------------------------------------------------------
  // A base's page
  // ---------------------------------------------------------------------------
  base: {
    loading: 'Loading the knowledge base…',
    missing: 'This knowledge base is not available to you.',
    missingBody: 'It may have been deleted, or it is not shared with you. The list shows the ones you can open.',
    failed: 'This knowledge base could not be loaded.',
    failedBody: 'Try again in a moment. Nothing about it has changed.',
    noDescription: 'No description.',
    ownedBy: 'Owned by',
    you: 'you',
    move: 'Move…',
    editLabel: (name: string): string => `Edit ${name}`,
    deleteLabel: (name: string): string => `Delete ${name}`,
    adminOnly:
      'You can open this only because you are an administrator. Opening its documents is recorded in the audit trail.',
    adminOnlyTitle: 'Not shared with you',
    waiting: (fmt: KnowledgeFmt, names: string[] | null): string =>
      names === null
        ? 'Waiting for review: sharing it with everyone.'
        : `Waiting for review: sharing it with ${fmt.formatList(names)}.`,
    requests: (fmt: KnowledgeFmt, count: number): string =>
      isOne(fmt, count)
        ? 'Someone asked to let an agent answer from it for everyone it reaches.'
        : `${fmt.formatCount(count)} people asked to let an agent answer from it for everyone it reaches.`,
    stats: {
      documents: 'documents',
      passages: 'passages',
      space: 'space',
      added: 'last added',
      never: 'never',
      of: (limit: string): string => `of ${limit}`
    },
    freshness: 'expected every',
    pictures: 'pictures',
    counting: 'Counting what this knowledge base can answer with…',
    census: (fmt: KnowledgeFmt, answering: number, total: number): string =>
      total === 0
        ? 'Nothing here yet. An agent asked about this will find nothing to cite.'
        : answering === total
          ? `All ${documents(fmt, total)} answering.`
          : answering === 0
            ? `${documents(fmt, total)}, and none of them can be cited yet.`
            : `${fmt.formatCount(answering)} of ${documents(fmt, total)} answering.`,
    /**
     * The freshness window, "90 days" rather than "2160h": the unit is chosen by arithmetic in
     * `knowledge-base.ts` and the form by CLDR here, never by `=== 1`.
     */
    window: {
      weeks: (fmt: KnowledgeFmt, count: number): string =>
        `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'week' : 'weeks'}`,
      days: (fmt: KnowledgeFmt, count: number): string =>
        `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'day' : 'days'}`,
      hours: (fmt: KnowledgeFmt, count: number): string =>
        `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'hour' : 'hours'}`
    }
  },

  tabs: {
    label: 'Knowledge base views',
    documents: 'Documents',
    access: 'Who can use it',
    folders: 'Connected folders',
    search: 'Search',
    needsAttention: 'needs attention',
    waiting: 'waiting'
  },

  // ---------------------------------------------------------------------------
  // Documents
  // ---------------------------------------------------------------------------

  /**
   * The word on a document's badge. The status is the server's vocabulary and this console keeps
   * its meaning, but not its spelling: `satisfies Record<KnowledgeDocument['status'], string>` so
   * a new status is a compile error rather than a raw `extracting` on a badge (D-9, F-8).
   *
   * `embedding` stays English in both locales: it is a term of art a Brazilian engineer reads in
   * English in every tool they use (D-15).
   */
  status: {
    pending: 'queued',
    extracting: 'reading',
    chunking: 'chunking',
    embedding: 'embedding',
    indexed: 'indexed',
    failed: 'failed',
    quarantined: 'quarantined',
    deleted: 'withdrawn'
  } satisfies Record<KnowledgeDocument['status'], string>,

  documents: {
    heading: 'Documents',
    count: (fmt: KnowledgeFmt, shown: number, total: number, filtered: boolean): string =>
      filtered ? `${fmt.formatCount(shown)} of ${documents(fmt, total)}` : documents(fmt, total),
    clearFilter: 'Clear filter',
    filterLabel: 'Filter documents by name',
    filterPlaceholder: 'Filter by name…',
    loading: 'Loading documents…',
    failed: 'The documents could not be loaded.',
    empty: 'No documents yet',
    emptyBody: (canMaintain: boolean): string =>
      canMaintain
        ? `Add ${FORMATS}. Each file is read and indexed when it arrives.`
        : 'Nothing has been added yet. Its maintainers add the documents.',
    nothingMatches: 'Nothing matches',
    nothingMatchesBody: 'No document here matches that filter.',
    columns: { document: 'Document', status: 'Status', passages: 'Passages', size: 'Size', indexed: 'Indexed' },
    actions: 'Actions',
    newest: 'The 500 most recent documents. Older ones are indexed and answering, and are not listed here.',
    remove: (title: string): string => `Remove ${title}`,
    removeTitle: (title: string): string => `Remove “${title}”?`,
    removePassages: (fmt: KnowledgeFmt, count: number): string =>
      count > 0
        ? `Its ${fmt.formatCount(count)} ${isOne(fmt, count) ? 'passage comes' : 'passages come'} out of every future answer. Citations already given keep working and say it was withdrawn.`
        : 'It holds no passages, so no answer changes.',
    removeFinal: 'This cannot be undone from here.',
    removeForGood: 'Remove for good',
    keep: 'Keep',
    removed: (title: string): string => `“${title}” was removed.`,
    notRemoved: (reason: string): string => `That document was not removed. ${reason}`,
    fullReason: 'Full reason',
    couldNotRead: 'This file could not be read.',
    parserWords: 'The reader’s own words:',
    /**
     * What to do about a failure, keyed by the `RemedyKey` `diagnoseFailure` classifies it as
     * (PRD-27 D-6). These used to sit inside the regex table that chose between them, which
     * made the choice of prose depend on the server's prose; the table now yields a key and
     * these are ordinary copy, translatable with everything else on the page.
     *
     * `satisfies Record<RemedyKey, string>` so a new remedy key is a compile error here rather
     * than a blank line where a maintainer expected their next move.
     */
    remedy: {
      scan_off:
        'This file holds pictures of text rather than text, and reading pictures needs OCR. Change how this knowledge base reads pictures — or, if it says OCR is not installed, ask an administrator — and then read the file again from here.',
      scan_unreadable:
        'OCR ran and found nothing it could read: the scan is too faint, too small or too skewed. Rescan it at a higher resolution, or add the version it was made from.',
      legacy: 'This is an older Office format. Open it in Office, save it as .docx, .xlsx or .pptx, and add that copy.',
      protected: 'The file is protected. Save an unprotected copy and add that instead.',
      timeout:
        'The reader ran out of time on this file — usually a very large scan or a deck full of pictures. Read it again when the queue is quieter, split it, or ask an administrator to raise the reading time budget if it keeps happening.',
      too_large:
        'The file is larger than the reader will take on — usually a very large embedded picture or a spreadsheet with millions of cells. Split it, or export the part that matters.',
      empty:
        'The file was read successfully and held nothing worth indexing — an empty document, or a template with no body text. Check you added the right file.',
      unsupported: `The reader does not handle this format. Convert it to one of ${FORMATS} and add it again.`,
      lost: 'The file was accepted but the process that reads it stopped before it started. Read it again from here.',
      superseded:
        'Another upload took over this document while it was being read, so this row is a leftover. The newer upload is the one that counts — remove this one.'
    } satisfies Record<RemedyKey, string>,
    /** When the cause is one this console has never seen: no guess, and the record below it. */
    noRemedy:
      'Nothing about this cause is recognised, so there is no reliable next step to suggest. The parser’s own words are below.',
    quarantined: 'Held by the malware scan.',
    quarantinedBody:
      'It was never read and nothing from it was indexed. Raise it with whoever runs security here rather than adding it again.',
    silent: 'Indexed, and holding no passages.',
    silentBody:
      'Search reads passages, so nothing in this file can ever be cited. Add it again; if it lands the same way, there is no text in it the reader can reach.',
    noPassagesBadge: 'no passages',
    connectedFolder: 'connected folder',
    // The admin's deliberate step before a read that is recorded (D-18).
    adminGateTitle: 'Its documents are not open yet',
    adminGateBody:
      'You are not one of the people it is shared with, so opening its documents is recorded in the audit trail.',
    adminGateOpen: 'Open its documents'
  },

  upload: {
    add: 'Add documents',
    /** `total === 1` is "is there only one file", not a plural form: the two are different sentences. */
    adding: (fmt: KnowledgeFmt, index: number, total: number, name: string): string =>
      total === 1 ? `Adding ${name}…` : `Adding ${fmt.formatCount(index)} of ${fmt.formatCount(total)} — ${name}…`,
    added: (fmt: KnowledgeFmt, count: number): string =>
      isOne(fmt, count) ? `${fmt.formatCount(count)} document added.` : `${fmt.formatCount(count)} documents added.`,
    stillReading: 'Still being read — the list says when each one is done.',
    reused: (name: string): string => `${name} was already here, so nothing new was stored.`,
    unreadable: (name: string): string => `${name} was added and could not be read — the row below says why.`,
    quarantined: (name: string): string => `${name} was set aside by the malware scan.`,
    notAdded: (name: string): string => `${name} was not added.`,
    tooLarge: (name: string, limit: string): string => `${name} is larger than ${limit}, the most one file may be.`,
    rateLimited: 'Too many files at once. Wait a moment, then add the rest again.',
    notTried: (fmt: KnowledgeFmt, count: number): string =>
      isOne(fmt, count)
        ? 'The next file was not tried.'
        : `The ${fmt.formatCount(count)} files after it were not tried.`,
    unchanged: 'Nothing else in this knowledge base changed.',
    clear: 'Clear',
    limits: (fmt: KnowledgeFmt, count: number, limit: number, used: string, space: string): string =>
      `${fmt.formatCount(count)} of ${fmt.formatCount(limit)} documents · ${used} of ${space}`,
    full: 'This knowledge base is full. Delete something it no longer needs, or ask an administrator to raise the limit.'
  },

  reading: {
    title: 'Pictures and scans',
    change: 'Change…',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    languages: 'Languages',
    languagesHint:
      'None chosen means all of them, in this order. Naming only the languages the documents are in makes reading pictures faster and more accurate.',
    noEngine: 'This deployment cannot read pictures, so pictures and scans are skipped whatever is chosen here.',
    appliesFrom:
      'Applies to documents added or read again from now on; the rest keep what they were read with until they are read again.',
    choices: {
      auto: {
        label: 'Read them when they look like content',
        hint: 'Scanned pages and pictures large enough to carry text are read; icons and logos are skipped.'
      },
      always: {
        label: 'Read every picture',
        hint: 'Slower on decks full of images, and worth it for screenshots of tables.'
      },
      off: {
        label: 'Text only',
        hint: 'Scans and pictures are counted but never read. A scan-only PDF cannot be indexed.'
      }
    },
    describe: {
      auto: (languages: string): string => `read when they look like content${languages}`,
      always: (languages: string): string => `every picture read${languages}`,
      off: 'text only'
    }
  },

  outdated: {
    readAgain: (fmt: KnowledgeFmt, count: number): string =>
      `Read ${
        isOne(fmt, count)
          ? `${fmt.formatCount(count)} outdated document`
          : `${fmt.formatCount(count)} outdated documents`
      } again`,
    queueing: 'Queueing…',
    queued: (fmt: KnowledgeFmt, count: number): string => `${documents(fmt, count)} queued`
  },

  /**
   * How much of a knowledge base answers — the five chip words, and the one sentence above them.
   *
   * `summary` takes the counts as a structural descriptor from `knowledge-console.ts` and composes
   * the sentence HERE, so the order of its two halves, the conjunction between the problems and
   * the sentence-final stop are all the locale's to decide (D-23). The verbs are written out per
   * count rather than for the singular alone: "2 documents was quarantined" is what the old
   * single-form entries produced the moment there were two (F-22).
   */
  health: {
    answering: 'answering',
    working: 'being read',
    silent: 'no passages',
    unreadable: 'could not be read',
    quarantined: 'set aside',

    nothingYet: 'Nothing here yet. An agent asked about this will find nothing to cite.',
    allAnswering: (fmt: KnowledgeFmt, total: number, held: number): string =>
      `${
        isOne(fmt, total)
          ? `All ${fmt.formatCount(total)} document answering`
          : `All ${fmt.formatCount(total)} documents answering`
      }, across ${passages(fmt, held)}.`,
    // `health.total` is at least two wherever this is reached: a single document either answers
    // (no problem) or is the problem, and the branch below says so without a ratio.
    nothingCited: 'Nothing here can be cited yet.',
    ratio: (fmt: KnowledgeFmt, answering: number, total: number, held: number): string =>
      `${fmt.formatCount(answering)} of ${fmt.formatCount(total)} documents answering, across ${passages(fmt, held)}.`,
    problem: {
      unreadable: (fmt: KnowledgeFmt, count: number): string =>
        isOne(fmt, count)
          ? `${fmt.formatCount(count)} document could not be read`
          : `${fmt.formatCount(count)} documents could not be read`,
      quarantined: (fmt: KnowledgeFmt, count: number): string =>
        isOne(fmt, count)
          ? `${fmt.formatCount(count)} document was quarantined`
          : `${fmt.formatCount(count)} documents were quarantined`,
      silent: (fmt: KnowledgeFmt, count: number): string =>
        isOne(fmt, count)
          ? `${fmt.formatCount(count)} document holds no passages`
          : `${fmt.formatCount(count)} documents hold no passages`,
      working: (fmt: KnowledgeFmt, count: number): string =>
        isOne(fmt, count)
          ? `${fmt.formatCount(count)} document is still being read`
          : `${fmt.formatCount(count)} documents are still being read`
    },
    /**
     * What works, then what does not. `Intl.ListFormat`, not a hand-rolled `' and '` join (D-19):
     * the conjunction, the separator and whether there is a serial comma are all the locale's to
     * decide, and English gains one ("could not be read, was quarantined, and is still being
     * read") while Portuguese has none.
     */
    summary: (fmt: KnowledgeFmt, lead: string, problems: string[]): string => `${lead} ${fmt.formatList(problems)}.`
  },

  // ---------------------------------------------------------------------------
  // Search
  // ---------------------------------------------------------------------------
  search: {
    label: 'Search this knowledge base',
    placeholder: 'Ask it what an agent would be asked…',
    run: 'Search',
    running: 'Searching…',
    hint: 'The same search an agent runs over this knowledge base, as you. Open a result to see the document it came from.',
    hintMaintainer:
      'The same search an agent runs over this knowledge base, as you. “How it was ranked” shows how each passage was found.',
    ranked: 'How it was ranked',
    rankedLead:
      'Each passage is found by matching its words, by matching its meaning, or both, and the two rankings are combined into one order.',
    strategyLabel: 'What to match',
    strategies: { hybrid: 'words and meaning', semantic: 'meaning only', lexical: 'words only' },
    readouts: { time: 'took', words: 'by words', meaning: 'by meaning', returned: 'returned' },
    byWords: (rank: number): string => `words #${rank}`,
    byMeaning: (rank: number): string => `meaning #${rank}`,
    combined: (score: string): string => `combined ${score}`,
    noResults: 'Nothing matched',
    noResultsBody:
      'Neither matching words nor matching meaning found a passage here. Try the words the documents themselves use.',
    results: (fmt: KnowledgeFmt, count: number): string => passages(fmt, count),
    failed: 'The search did not run.'
  },

  // ---------------------------------------------------------------------------
  // Who can use it
  // ---------------------------------------------------------------------------
  access: {
    title: 'Who can use it',
    lead: 'The people who can use it can find it, open it and search it, and get answers from it through any agent they use.',
    sectionLead:
      'Groups you manage go live when you save. Everyone, or a group you do not manage, waits for an administrator’s review — the rest of the change still goes live.',
    sectionLeadAdmin: 'You are an administrator, so what you choose goes live when you save.',
    loading: 'Loading who can use it…',
    failed: 'Who can use it could not be loaded.',
    failedBody: 'Nothing has changed. Try again in a moment.',
    onlyPerson: (name: string): string => `Only ${name}`,
    save: 'Save',
    sendForReview: 'Send for review',
    saveAndSend: 'Save and send for review',
    saving: 'Saving…',
    discard: 'Discard changes',
    foldersAskAgain: (fmt: KnowledgeFmt, count: number): string =>
      isOne(fmt, count)
        ? 'Its connected folder will ask you to confirm who can search what it loads before its next check.'
        : `Its ${fmt.formatCount(count)} connected folders will ask you to confirm who can search what they load before their next check.`,
    /** When only the part that waits for review widens it: the folders ask once that is applied. */
    foldersAskAfterApproval: (fmt: KnowledgeFmt, count: number): string =>
      isOne(fmt, count)
        ? 'If an administrator approves, its connected folder will ask you to confirm who can search what it loads before its next check.'
        : `If an administrator approves, its ${fmt.formatCount(count)} connected folders will ask you to confirm who can search what they load before their next check.`,
    saved: 'Saved. It is live now.',
    nothingChanged: 'Nothing changed — that is who can use it already.',
    sentForReview: 'Sent for review.',
    savedAndSent: 'Saved — what you could change is live now.',
    waitingFor: 'Waiting for review:',
    waitingEveryone: 'sharing with everyone',
    reviewNote: 'An administrator decides, and you are told either way.',
    stale: 'Someone changed who can use it while you were editing. It has been reloaded — check it, then save again.',
    pendingTitle: 'Waiting for review',
    pendingBody: (when: string): string =>
      `Asked ${when}. Until an administrator decides, who can use it is what is shown below.`,
    pendingEveryone: 'Sharing it with everyone',
    pendingGroups: 'Sharing it with',
    withdraw: 'Withdraw request',
    withdrawing: 'Withdrawing…',
    withdrawn: 'Request withdrawn. Nothing changed about who can use it.',
    sharedNow: 'Shared now',
    sharedBy: (name: string | null, byAdministrator: boolean, when: string): string =>
      `${name ? `Shared by ${name}${byAdministrator ? ', as an administrator' : ''}` : 'Shared'} · ${when}`,
    approvedBy: (name: string): string => `approved by ${name}`
  },

  agents: {
    title: 'Agents that use it',
    lead: 'An agent answers from it only for the people who can use it — unless you let it answer for everyone it reaches.',
    sectionLead:
      'Every agent it is attached to: how many people each one reaches, and how many of them cannot use this knowledge base themselves.',
    loading: 'Loading the agents that use it…',
    failed: 'The agents that use it could not be loaded.',
    empty: 'No agent uses it yet.',
    emptyBody: 'When someone attaches it to an agent they build, the agent appears here.',
    maintainedBy: (owner: string): string => `Maintained by ${owner}`,
    groupManagers: (group: string): string => `${group}’s managers`,
    /** Beside an agent that is not live, in the words the builder uses for the same state. */
    state: AGENT_STATE,
    reaches: (fmt: KnowledgeFmt, reach: number, beyond: number, base: string): string =>
      beyond === 0
        ? `Reaches ${people(fmt, reach)} — all of them can use ${base}.`
        : `Reaches ${people(fmt, reach)} — ${fmt.formatCount(beyond)} of them cannot use ${base} themselves.`,
    /** Whom an agent that is not live reaches: nobody, and why. A code is compared, never prose. */
    notLive: (state: string): string =>
      state === 'archived'
        ? 'Retired — nobody can start a conversation with it.'
        : state === 'suspended'
          ? 'Paused by an administrator — nobody can use it now.'
          : 'Not shared with anyone yet.',
    forEveryone: 'Answers from it for everyone it reaches',
    onlyUsers: 'Answers from it only for people who can use it',
    allowedBy: (name: string | null, byAdministrator: boolean, when: string): string =>
      `${name ? `allowed by ${name}${byAdministrator ? ', as an administrator' : ''}` : 'allowed'} · ${when}`,
    detached: 'It no longer uses this knowledge base. The permission does nothing until it is attached again.',
    letAnswer: 'Let it answer for everyone it reaches',
    stop: 'Stop',
    stopConfirm: (agent: string, base: string): string =>
      `Stop ${agent} answering from ${base} for everyone it reaches? From their next message, people who cannot use ${base} stop getting answers from it. Answers already given are not recalled.`,
    stopping: 'Stopping…',
    keep: 'Keep',
    asked: (name: string, origin: 'maker' | 'reader'): string =>
      `${name} asked you to let it answer for everyone it reaches${origin === 'maker' ? ', while building it' : ' — they use it, and it could not answer them from this'}.`,
    allow: 'Allow',
    decline: 'Decline',
    /** Appended, visually hidden, to a row's buttons so each names the agent it acts on. */
    forAgent: (agent: string): string => ` — ${agent}`,
    requestLabel: (name: string): string => `${name}’s request`,
    waiting: (fmt: KnowledgeFmt, count: number): string =>
      isOne(fmt, count)
        ? 'Someone is waiting for you to decide whether an agent may answer from it for everyone it reaches.'
        : `${fmt.formatCount(count)} people are waiting for you to decide whether an agent may answer from it for everyone it reaches.`,
    showRequests: 'Show',
    /** A request opened from a notification that has since stopped waiting (the quiet line). */
    decided: {
      approved: (asker: string, by: string | null, when: string): string =>
        `${asker}’s request was already decided: allowed${by ? ` by ${by}` : ''} · ${when}.`,
      declined: (asker: string, by: string | null, when: string): string =>
        `${asker}’s request was already decided: declined${by ? ` by ${by}` : ''} · ${when}.`,
      withdrawn: (asker: string): string => `${asker} withdrew this request, so there is nothing to decide.`,
      closed: (asker: string, reason: string): string => `${asker}’s request was closed — ${reason}.`,
      detached: (agent: string): string =>
        `This request is still open, but ${agent} no longer uses this knowledge base, so there is nothing to allow.`,
      gone: 'That request is no longer open, so there is nothing to decide.'
    },
    /** Typed against the wire enum, so a new closing reason is a compile error, not a blank. */
    closedReason: {
      base_deleted: 'the knowledge base was deleted',
      agent_retired: 'the agent was retired',
      group_deleted: 'a group it depended on was deleted',
      no_longer_eligible: 'it no longer applies'
    } satisfies Record<NonNullable<LendingRequest['closedReason']>, string>
  },

  /** The decision a maintainer reads before an agent answers from a base for everyone it reaches (D-16). */
  lend: {
    title: (agent: string): string => `Let ${agent} answer for everyone it reaches?`,
    allowTitle: (name: string, agent: string): string => `Allow ${name}’s request for ${agent}?`,
    lead: (agent: string, base: string): string =>
      `Today ${agent} answers from ${base} only for the people who can use ${base}. This lets it answer from it for everyone it reaches.`,
    /**
     * Who is accountable, from the owner rather than from a phrase built elsewhere.
     *
     * It used to take an already-worded "Finance’s managers", which is an English possessive
     * dropped into the middle of a sentence — unrenderable in a language that says the same
     * thing with a relative clause, and unstorable as a record (PRD-27 D-20).
     */
    maintainedBy: (agent: string, owner: { kind: 'user' | 'group'; name: string }): string =>
      lendMaintainedBy(agent, owner),
    acts: LEND_ACTS,
    delegates: LEND_DELEGATES,
    actsAndDelegates: LEND_ACTS_AND_DELEGATES,
    answersOnly: LEND_ANSWERS_ONLY,
    reachNow: (fmt: KnowledgeFmt, reach: number, beyond: number, base: string): string =>
      lendReachNow(fmt, reach, beyond, base),
    /**
     * For an agent that is not live — a draft, retired or paused. The server counts its reach as
     * its maintainers and nobody beyond them, which is a default, not a fact worth agreeing to.
     */
    reachNotLive: (base: string): string => lendReachNotLive(base),
    grows: (base: string): string => lendGrows(base),
    recall: LEND_RECALL,
    /**
     * The whole disclosure, from the values that are persisted as the record of it.
     *
     * Five facts, because each is one a maintainer has been surprised by: who is accountable for
     * the agent, whether it can act, how many people it reaches today and how many of them
     * cannot use the base, that the reach grows without asking, and that stopping later does not
     * take back what was already said.
     *
     * Rendering the dialog and replaying the record run this one function, so "the Portuguese
     * acknowledgement said the same thing as the English one" is a property of the code rather
     * than a claim about two translations (PRD-27 D-7).
     */
    disclosure: (fmt: KnowledgeFmt, ack: LendingAck): string[] => [
      lendMaintainedBy(ack.agent, { kind: ack.ownerKind, name: ack.ownerName }),
      lendCapability(ack.acts, ack.delegates),
      // Nobody uses an agent that is not live, whatever its maintainers could: no count to agree to.
      ack.live ? lendReachNow(fmt, ack.reach, ack.beyond, ack.base) : lendReachNotLive(ack.base),
      lendGrows(ack.base),
      LEND_RECALL
    ],
    note: (name: string): string => `${name} wrote:`,
    confirm: 'Let it answer',
    allow: 'Allow',
    working: 'Saving…',
    cancel: 'Cancel'
  },

  decline: {
    title: (name: string): string => `Decline ${name}’s request?`,
    lead: (agent: string, base: string, name: string): string =>
      `${agent} keeps answering from ${base} only for the people who can use it. ${name} is told, with your note.`,
    note: 'Note',
    noteHint: 'Required — say why, so they can act on it.',
    submit: 'Decline',
    working: 'Declining…',
    cancel: 'Cancel'
  },

  // ---------------------------------------------------------------------------
  // Owners and limits
  // ---------------------------------------------------------------------------
  limits: {
    unlimited: 'Your own knowledge bases have no limits, because you are an administrator.',
    you: (fmt: KnowledgeFmt, used: number, limit: number): string =>
      `You have ${fmt.formatCount(used)} of ${bases(fmt, limit)}.`,
    group: (fmt: KnowledgeFmt, group: string, used: number, limit: number): string =>
      `${group} has ${fmt.formatCount(used)} of ${bases(fmt, limit)}.`,
    perBase: (fmt: KnowledgeFmt, docs: number, space: string): string =>
      `Each can hold ${documents(fmt, docs)} and ${space}.`,
    /** What the owner has, then what each base may hold — two sentences, joined as this locale joins them. */
    holdsAndPerBase: (holds: string, perBase: string): string => `${holds} ${perBase}`,
    youFull: (fmt: KnowledgeFmt, limit: number): string =>
      `You already have ${bases(fmt, limit)}, the most you can have. Delete one you no longer need, or ask an administrator to raise the limit.`,
    groupFull: (fmt: KnowledgeFmt, group: string, limit: number): string =>
      `${group} already has ${bases(fmt, limit)}, the most it can have. Choose another owner, or ask an administrator to raise the limit.`,
    unknown: 'Their own limits apply. If this would take them over, it is refused and nothing changes.',
    meter: 'Knowledge bases',
    meterValue: (fmt: KnowledgeFmt, used: number, limit: number): string =>
      `${fmt.formatCount(used)} of ${fmt.formatCount(limit)}`
  },

  create: {
    title: 'New knowledge base',
    lead: 'A place for the documents an agent may cite. At first only the people who maintain it can use it.',
    name: 'Name',
    namePlaceholder: 'Travel policies',
    nameHint: 'Up to 120 characters. People see this wherever it is shared.',
    description: 'Description (optional)',
    descriptionPlaceholder: 'What an agent will find here',
    afterwards: 'You choose who else can use it afterwards, on its “Who can use it” tab.',
    submit: 'Create knowledge base',
    submitting: 'Creating…',
    cancel: 'Cancel',
    forPerson: (name: string): string =>
      `It is made yours first and then given to ${name}. Their limits apply; if it would take them over, it stays yours.`,
    notMoved: (name: string, reason: string): string =>
      `It was made, but it could not be given to ${name}: ${reason} It is yours for now — move it from its page.`,
    open: 'Open it'
  },

  move: {
    title: (name: string): string => `Move ${name}`,
    lead: 'Moving never changes who can use it. The new owner’s limits apply.',
    maintainers: {
      you: 'You will maintain it.',
      person: (name: string): string => `${name} will maintain it.`,
      group: (group: string): string => `${group}’s managers will maintain it.`
    },
    overLimit: (fmt: KnowledgeFmt, docs: number, space: string): string =>
      `It holds more than a new owner’s knowledge base may (${documents(fmt, docs)} and ${space}), so nothing new can be added until some of it is removed.`,
    folders:
      'Its connected folders will ask whoever maintains it to confirm who can search what they load before their next check.',
    unknown: 'Its limits apply. If this would take it over, the move is refused and nothing changes.',
    submit: 'Move',
    submitting: 'Moving…',
    cancel: 'Cancel',
    choose: 'Choose where it goes.'
  },

  edit: {
    title: (name: string): string => `Edit ${name}`,
    lead: 'Renaming it never changes who can use it or which agents use it.',
    submit: 'Save',
    submitting: 'Saving…',
    cancel: 'Cancel'
  },

  remove: {
    title: (name: string): string => `Delete ${name}?`,
    lead: 'This cannot be undone. Read what changes before you confirm.',
    documents: (fmt: KnowledgeFmt, count: number): string =>
      count === 0
        ? 'It holds no documents.'
        : `Its ${documents(fmt, count)} stop answering, for everyone, and cannot be brought back from here.`,
    agents: 'These agents use it. They keep working without it, and their maintainers are told:',
    noAgents: 'No agent uses it.',
    folders: (fmt: KnowledgeFmt, count: number): string =>
      isOne(fmt, count)
        ? 'Its connected folder stops reading, and nothing in the place its files come from is touched.'
        : `Its ${fmt.formatCount(count)} connected folders stop reading, and nothing in the places their files come from is touched.`,
    requests: 'Requests about it are closed, and the people who asked are told.',
    typeName: (name: string): string => `Type ${name} to confirm`,
    submit: 'Delete knowledge base',
    submitting: 'Deleting…',
    cancel: 'Cancel'
  },

  // ---------------------------------------------------------------------------
  // One document, opened up (PRD-07 KB-019) — stranded in `DocumentDrawer.tsx` until PRD-27 F-14
  // ---------------------------------------------------------------------------
  drawer: {
    /** The dialog's accessible name: the document's own title, then what this panel is. */
    label: (title: string): string => `${title} — document detail`,
    closeScrim: 'Close document detail',
    removeFooter: 'Removing takes its passages out of every future answer.',
    /**
     * A confirm button's accessible name: the words on the button, then the document it acts on.
     * Neither is a grammatical part of the other — this is a NAME, not a sentence — so the
     * separator is all a translator has to decide (WCAG 2.5.3 keeps the visible words first).
     */
    actionOn: (action: string, title: string): string => `${action}: ${title}`,

    answering: (fmt: KnowledgeFmt, count: number): string => `Answering. ${passages(fmt, count)} an agent may cite.`,
    beingRead: (status: string): string => `Being read — currently ${status}. Nothing from this file can be cited yet.`,
    silentTitle: 'Indexed, but holding no passages.',
    silentBody:
      'This file is in the catalogue and cannot be cited — retrieval reads passages, and this has none. Add it again; if it lands the same way, the file has no text the parser can reach.',
    quarantinedTitle: 'Set aside: it carries active content.',
    quarantinedBody:
      'It was never read and nothing from it was indexed. Macros, embedded scripts and launch actions are what this catches; strip them at the source — printing to a new PDF or saving without macros usually does — and add the clean copy. There is no way to read this copy as it is.',
    fileKept:
      'The file is kept. Once the cause is fixed — OCR turned on, the engine installed, a newer reader deployed — “Read again” below re-reads this copy; a corrected file is added like any other.',
    reading: 'Reading…',
    readAgain: 'Read again',

    /** What was read, and what was left out (PRD-24 ING-014). */
    whatWasRead: 'What was read',
    readouts: {
      structure: 'structure',
      pictures: 'pictures',
      ocr: 'ocr',
      notRead: 'not read',
      readIn: 'read in',
      searchLanguage: 'search language'
    },
    proseOnly: 'prose only',
    noPictures: 'none',
    /** The parts of a document the reader found, each a noun phrase the row joins with commas. */
    structure: {
      headings: (fmt: KnowledgeFmt, count: number): string =>
        `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'heading' : 'headings'}`,
      tables: (fmt: KnowledgeFmt, count: number): string =>
        `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'table' : 'tables'}`,
      listItems: (fmt: KnowledgeFmt, count: number): string =>
        `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'list item' : 'list items'}`,
      notes: (fmt: KnowledgeFmt, count: number): string =>
        `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'speaker note' : 'speaker notes'}`,
      pages: (fmt: KnowledgeFmt, count: number): string =>
        `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'page' : 'pages'}`,
      slides: (fmt: KnowledgeFmt, count: number): string =>
        `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'slide' : 'slides'}`,
      sheets: (fmt: KnowledgeFmt, count: number): string =>
        `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'sheet' : 'sheets'}`
    },
    /** Readouts stacked in a row, joined by the locale's own comma — never a sentence. */
    joinParts: (parts: readonly string[]): string => parts.join(', '),
    /** Pictures found, how many held text, and how sure OCR was. A fraction through `Intl`. */
    pictures: (found: string, withText: string, confidence: string | null): string =>
      `${found} found · ${withText} with text${confidence === null ? '' : ` · ${confidence} sure`}`,
    /** The engine and the languages it was given — both identifiers, never translated (D-17). */
    ocrValue: (engine: string, languages: string, scannedPages: string | null): string =>
      `${engine} (${languages})${scannedPages === null ? '' : ` · ${scannedPages}`}`,
    scannedPages: (fmt: KnowledgeFmt, count: number): string =>
      `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'scanned page' : 'scanned pages'}`,
    hiddenSlides: (fmt: KnowledgeFmt, count: number): string =>
      `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'hidden slide' : 'hidden slides'}`,
    hiddenSheets: (fmt: KnowledgeFmt, count: number): string =>
      `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'hidden sheet' : 'hidden sheets'}`,
    /** Why pictures were passed over. Each is a whole clause; the sentence below collects them. */
    skipped: {
      tiny: (fmt: KnowledgeFmt, count: number): string => `${fmt.formatCount(count)} too small to hold text`,
      duplicate: (fmt: KnowledgeFmt, count: number): string => `${fmt.formatCount(count)} repeated`,
      unsupported: (fmt: KnowledgeFmt, count: number): string =>
        `${fmt.formatCount(count)} in a format OCR cannot open`,
      off: (fmt: KnowledgeFmt, count: number): string =>
        `${fmt.formatCount(count)} not read (OCR is off for this knowledge base)`,
      unavailable: (fmt: KnowledgeFmt, count: number): string =>
        `${fmt.formatCount(count)} not read (no OCR engine installed)`,
      cap: (fmt: KnowledgeFmt, count: number): string => `${fmt.formatCount(count)} past the per-document limit`
    },
    /** `reasons` is a list of complete clauses, so the sentence's grammar does not depend on it. */
    picturesSkipped: (reasons: readonly string[]): string => `Pictures skipped: ${reasons.join('; ')}.`,
    discarded: (fmt: KnowledgeFmt, count: number): string =>
      `${fmt.formatCount(count)} ${isOne(fmt, count) ? 'block' : 'blocks'} of recognised text discarded as too uncertain to index.`,

    /** Where a synced document came from (PRD-25), when the provider did not name its surface. */
    aConnectedFolder: 'a connected folder',
    folderGone: 'The folder it came from is no longer connected; the document stays until somebody removes it.',

    /** The stamps that make a citation auditable (KB-003). The values are identifiers (D-17). */
    lineage: 'Lineage',
    lineageLabels: {
      status: 'status',
      size: 'size',
      passages: 'passages',
      type: 'type',
      pages: 'pages',
      language: 'language',
      author: 'author',
      written: 'written',
      parser: 'parser',
      embedding: 'embedding',
      pipeline: 'pipeline',
      contentHash: 'content hash',
      added: 'added',
      indexed: 'indexed'
    },
    undetected: 'undetected',
    unstamped: 'unstamped',
    pipelineOutdated: (version: string): string => `${version} · outdated`,

    /** The corpus as the machine holds it. */
    passagesHeading: 'Passages',
    passageCount: (fmt: KnowledgeFmt, count: number): string => passages(fmt, count),
    matchCount: (fmt: KnowledgeFmt, matched: number, total: number): string =>
      `${fmt.formatCount(matched)} of ${fmt.formatCount(total)} match`,
    findLabel: 'Find in passages',
    findPlaceholder: 'Find in passages…',
    loadingPassages: 'Reading passages…',
    passagesFailed: 'The passages could not be read.',
    noPassages: 'No passages',
    noPassagesBody: 'Nothing was indexed from this file, so there is nothing an agent could cite from it.',
    noMatch: 'No passage contains that text.',
    /** `tok` is the unit on a telemetry chip, not prose (D-15). */
    tokens: (fmt: KnowledgeFmt, count: number): string => `${fmt.formatCount(count)} tok`,
    showMore: (fmt: KnowledgeFmt, more: number, total: number): string =>
      `Show ${fmt.formatCount(more)} more of ${fmt.formatCount(total)}`,
    capped: (fmt: KnowledgeFmt, limit: number): string =>
      `The first ${fmt.formatCount(limit)} passages of this document. Anything beyond that is indexed and not listed here.`
  },

  genericError: 'Something went wrong. Try again.'
}
